/* The application: mailboxes in a sidebar, the list, the message.
 *
 * **Two states on one URL.** The page is a document until the first file is
 * opened; then `data-mb-mode="app"` on the body turns it into an application
 * filling the window (`app.css`, "Mboxray"). Closing the last mailbox turns it
 * back. Reload starts clean: nothing is kept anywhere (the owner's decision).
 *
 * **Several files, opened one after another, never merged.** Each is a
 * `Mailbox` with its own worker and its own remembered place.
 */

import { MailboxReader } from "../client.js";
import { Mailbox } from "./mailbox.js";
import { MessageList } from "./list.js";
import { messageView } from "./view.js";
import { printMessages } from "./print.js";
import { framedMessage } from "./remote.js";
import { conversationView } from "./conversation.js";
import { clearMarks, markMatches, setMarkedText } from "./highlight.js";
import { fromDrop, fromFilePicker, fromFolderPicker, uniqueName, sameFile } from "./files.js";
import { count, safeFileName } from "./format.js";

const WIDE = "(min-width: 64rem)"; // Tailwind's `lg`, where all three panes show

export function startApp(document) {
  const $ = (name) => document.querySelector(`[data-mb="${name}"]`);
  const body = document.body;
  const app = $("app");
  const wide = window.matchMedia(WIDE);
  const baseTitle = document.title;
  const productName = $("document")?.querySelector("h1")?.textContent?.trim() || "Mboxray";

  const mailboxes = [];
  let current = null;
  let viewUrls = [];
  let showing = 0;
  let typing = 0; // the search box's debounce
  let frames = []; // picture frames on show, for disposal
  let shownConversation = null;

  // **Loading pictures is the reader's choice, per message or for the session**
  // (step 6). The session setting lives in this variable and nowhere else, so
  // a reload forgets it, as it forgets everything (the owner's decision).
  let alwaysLoadImages = false;
  const framePath = `${location.pathname.replace(/\/+$/, "")}/frame`;

  const list = new MessageList($("list"), $("row-template"));

  // ------------------------------------------------------------ rendering

  let queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      render();
    });
  }

  function render() {
    renderSidebar();
    if (!current) return;

    const listed = current.listed();
    const total = current.total;

    let label;
    if (current.error === "empty") label = "No messages found";
    else if (current.error) label = "This file could not be read";
    else if (current.indexing) label = `Reading, ${count(total)} so far`;
    else if (current.conversations) {
      // Rows are conversations now, so say both numbers.
      const shown = `${count(listed.length)} ${listed.length === 1 ? "conversation" : "conversations"}`;
      label = current.isFiltering()
        ? `${shown} match${current.searching ? ", still searching" : ""}`
        : `${shown}, ${count(total)} messages`;
    }
    else if (current.isFiltering()) label = `${count(listed.length)} of ${count(total)}${current.searching ? ", still searching" : ""}`;
    else label = `${count(total)} ${total === 1 ? "message" : "messages"}`;
    $("count").textContent = label;

    const selected = current.checked.size;
    $("listhead").classList.toggle("hidden", selected > 0);
    $("listhead").classList.toggle("flex", selected === 0);
    $("selectionbar").classList.toggle("hidden", selected === 0);
    $("selectionbar").classList.toggle("flex", selected > 0);
    $("selected-count").textContent = `${count(selected)} selected`;

    const allTicked = listed.length > 0 && listed.every((id) => current.tickState(id) === "all");
    const someTicked = !allTicked && listed.some((id) => current.tickState(id) !== "none");
    for (const box of [$("select-all"), $("select-all-2")]) {
      box.checked = allTicked;
      box.indeterminate = someTicked;
      box.disabled = listed.length === 0;
    }

    $("sort-label").textContent = current.newestFirst ? "Newest first" : "Oldest first";
    $("view-messages").setAttribute("aria-pressed", String(!current.conversations));
    $("view-conversations").setAttribute("aria-pressed", String(current.conversations));

    const progress = current.searching ? current.searchProgress : 0;
    $("progress").style.transform = `scaleX(${progress})`;

    const empty = $("list-empty");
    const message =
      current.error === "empty" ? "This file has no messages in it. Is it an mbox file?"
      : current.error ? "This file could not be read."
      : listed.length === 0 && current.isFiltering() && current.searching ? "Searching inside messages."
      : listed.length === 0 && current.isFiltering() ? "Nothing matches that search."
      : "";
    empty.textContent = message;
    empty.classList.toggle("hidden", !message);

    list.show(current);
  }

  const sidebarItems = new Map(); // mailbox -> li

  function renderSidebar() {
    const ul = $("mailboxes");

    for (const [mailbox, li] of sidebarItems) {
      if (!mailboxes.includes(mailbox)) {
        li.remove();
        sidebarItems.delete(mailbox);
      }
    }

    for (const mailbox of mailboxes) {
      let li = sidebarItems.get(mailbox);

      if (!li) {
        li = $("mailbox-template").content.firstElementChild.cloneNode(true);
        li.querySelector('[data-mb="mailbox-select"]').addEventListener("click", () => select(mailbox, true));
        li.querySelector('[data-mb="mailbox-close"]').addEventListener("click", () => close(mailbox));
        sidebarItems.set(mailbox, li);
        ul.appendChild(li);
      }

      const find = (name) => li.querySelector(`[data-mb="${name}"]`);
      const row = li.firstElementChild;
      if (mailbox === current) row.setAttribute("data-current", "");
      else row.removeAttribute("data-current");
      find("mailbox-select").setAttribute("aria-current", String(mailbox === current));
      find("mailbox-name").textContent = mailbox.name;
      find("mailbox-name").title = mailbox.file.name;
      find("mailbox-count").textContent = mailbox.error ? "" : count(mailbox.total);
      find("mailbox-close").setAttribute("aria-label", `Close ${mailbox.name}`);
      find("mailbox-progress").classList.toggle("hidden", !mailbox.indexing);
      find("mailbox-progress").classList.toggle("block", mailbox.indexing);
      find("mailbox-progress-bar").style.transform = `scaleX(${mailbox.progress})`;
      find("mailbox-error").textContent = mailbox.error === "empty" ? "No messages found" : mailbox.error ? "Could not be read" : "";
      find("mailbox-error").classList.toggle("hidden", !mailbox.error);
    }
  }

  // ------------------------------------------------------------ mailboxes

  function openAll(entries) {
    if (!entries.length) {
      showLandingError("That did not contain an mbox file. From Apple Mail, choose the exported folder.");
      return;
    }

    let last = null;
    for (const { file, name } of entries) {
      const existing = mailboxes.find((mailbox) => sameFile(mailbox.file, file));
      if (existing) {
        last = existing;
        continue;
      }

      const taken = new Set(mailboxes.map((mailbox) => mailbox.name));
      const mailbox = new Mailbox(uniqueName(name, taken), file, (_, events) => new MailboxReader(file, events), schedule);
      mailboxes.push(mailbox);
      last = mailbox;
    }

    enterApp();
    select(last, true);
  }

  function select(mailbox, showList) {
    if (current && current !== mailbox) {
      current.scrollTop = $("list").scrollTop;
      // A search typed a moment ago belongs to the mailbox it was typed in.
      clearTimeout(typing);
      current.setQuery($("query").value);
    }

    current = mailbox;
    $("query").value = mailbox.query.text;
    $("query").placeholder = `Search ${mailbox.name}`;
    document.title = `${mailbox.name} · ${productName}`;
    list.show(mailbox);
    showMessage(mailbox.current);
    if (showList && !wide.matches) setView("list");
    render();
  }

  function close(mailbox) {
    const index = mailboxes.indexOf(mailbox);
    mailbox.close();
    mailboxes.splice(index, 1);

    if (!mailboxes.length) {
      current = null;
      leaveApp();
      return;
    }

    if (mailbox === current) select(mailboxes[Math.min(index, mailboxes.length - 1)], false);
    else render();
  }

  function enterApp() {
    body.dataset.mbMode = "app";
    app.hidden = false;
  }

  function leaveApp() {
    delete body.dataset.mbMode;
    app.hidden = true;
    document.title = baseTitle;
    clearReader();
    renderSidebar();
  }

  function showLandingError(text) {
    const error = $("landing-error");
    error.textContent = text;
    error.classList.remove("hidden");
    if (body.dataset.mbMode === "app") alertInApp(text);
  }

  function alertInApp(text) {
    $("list-empty").textContent = text;
    $("list-empty").classList.remove("hidden");
  }

  // ------------------------------------------------------------ the message

  function clearReader() {
    for (const frame of frames) frame?.dispose();
    frames = [];
    shownConversation?.dispose();
    shownConversation = null;
    for (const url of viewUrls) URL.revokeObjectURL(url);
    viewUrls = [];
    $("reader").replaceChildren();
    $("reader").classList.add("hidden");
    $("reader-empty").classList.remove("hidden");
  }

  async function showMessage(id) {
    const mailbox = current;
    const token = ++showing;

    if (id === null || id === undefined || !mailbox) {
      clearReader();
      return;
    }

    // In conversation mode a message with company is shown with its company.
    const conversation = mailbox.conversations ? mailbox.conversationOf(id) : null;
    if (conversation && conversation.count > 1) {
      clearReader();
      shownConversation = conversationView(
        { conversation: $("conversation-template"), item: $("conversation-item-template") },
        mailbox, conversation,
        // Bodies arrive after the view is shown, so the first match is sought again.
        (member, body) => renderInto(mailbox, member, body, true).then((handle) => (revealFirstMatch(), handle)),
        {
          back: () => history.state?.mbView === "message" ? history.back() : setView("list"),
          pdf: (ids) => exportPdf(mailbox, ids),
        });
      showInReader(shownConversation.element);
      return;
    }

    const body = document.createElement("div");
    const handle = await renderInto(mailbox, id, body, false, token);
    if (handle === undefined) return; // a newer choice won, or the mailbox closed
    clearReader();
    frames.push(handle);
    showInReader(body);
  }

  function showInReader(element) {
    $("reader").replaceChildren(element);
    $("reader").classList.remove("hidden");
    $("reader-empty").classList.add("hidden");
    $("readerpane").scrollTop = 0;
    revealFirstMatch();
  }

  /**
   * **The open message follows the search box.** Marks are drawn when a
   * message is rendered, so without this a new search left the open message
   * showing the previous search's marks, or none (the owner, 2026-09-18).
   * Re-marks the subject and every rendered body in the reader, then brings
   * the first new match into view.
   */
  function remarkReader() {
    if (!current) return;
    const terms = current.query.terms;
    for (const subject of $("reader").querySelectorAll('[data-mb="msg-subject"], [data-mb="conv-subject"]')) {
      setMarkedText(subject, subject.textContent, terms);
    }
    for (const host of $("reader").querySelectorAll('[data-mb="msg-body"]')) {
      if (!host.shadowRoot) continue; // a message in the picture frame is not marked
      clearMarks(host.shadowRoot);
      markMatches(host.shadowRoot, terms);
    }
    revealFirstMatch();
  }

  /** Brings the first marked search word into view, if the message has one. */
  function revealFirstMatch() {
    requestAnimationFrame(() => {
      for (const host of $("reader").querySelectorAll('[data-mb="msg-body"]')) {
        const mark = host.shadowRoot?.querySelector("mark");
        if (mark) {
          mark.scrollIntoView({ block: "center", behavior: "instant" });
          return;
        }
      }
    });
  }

  /**
   * Draws one message into `body`: on its own (compact false) or inside a
   * conversation. Returns the picture frame's handle, or null; undefined when
   * a newer choice made this one moot.
   */
  async function renderInto(mailbox, id, body, compact, token = showing) {
    let parsed;
    try {
      parsed = await mailbox.reader.message(id);
    } catch {
      return undefined; // closed while loading
    }
    if (!compact && (token !== showing || mailbox !== current)) return undefined;

    mailbox.withImages ??= new Set();
    const redraw = () => (compact ? renderInto(mailbox, id, body, true) : showMessage(id));
    const shown = messageView($("message-template"), $("attachment-template"), parsed, viewUrls, {
      // A message showing its pictures prints from its frame, pictures and all.
      // A message with its pictures prints from its own frame, which asks with
      // its own button: a click on this page does not count inside the frame.
      pdf: (frame) => frame ? frame.print(safeFileName(parsed.subject)) : exportPdf(mailbox, [id]),
      eml: () => downloadEml(mailbox, id, parsed.subject),
      back: () => history.state?.mbView === "message" ? history.back() : setView("list"),
      loadImages: () => {
        mailbox.withImages.add(id);
        redraw();
      },
      blockImages: () => {
        mailbox.withImages.delete(id);
        alwaysLoadImages = false;
        redraw();
      },
      setAlways: (on) => {
        alwaysLoadImages = on;
        redraw();
      },
    }, {
      load: alwaysLoadImages || mailbox.withImages.has(id),
      always: alwaysLoadImages,
      framePath,
      frame: framedMessage,
      compact,
      terms: mailbox.query.terms,
    });

    body.replaceChildren(shown.element);
    if (compact && shown.framed) frames.push(shown.framed);
    return shown.framed;
  }

  function open(id) {
    if (!current) return;
    current.current = id;
    list.draw();
    showMessage(id);
    if (!wide.matches) setView("message");
  }

  async function downloadEml(mailbox, id, subject) {
    const bytes = await mailbox.reader.raw(id);
    const url = URL.createObjectURL(new Blob([bytes], { type: "message/rfc822" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeFileName(subject)}.eml`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  // ------------------------------------------------------------ export

  /** The bottom notice, for progress. */
  function progress(text) {
    const toast = $("toast");
    if (text === null) {
      toast.classList.replace("flex", "hidden");
      return;
    }
    $("toast-text").textContent = text;
    toast.classList.replace("hidden", "flex");
  }

  async function exportPdf(mailbox, ids) {
    try {
      await printMessages({
        body, container: $("print"), template: $("print-template"), progress,
        dialog: {
          element: $("print-dialog"), ready: $("print-ready"), go: $("print-go"),
          cancel: $("print-cancel"), backdrop: $("print-backdrop"),
        },
      }, mailbox, ids);
    } catch {
      progress(null);
      alertInApp("Those messages could not be prepared for printing.");
    }
  }

  // ------------------------------------------------------------ narrow screens

  /**
   * **One pane at a time below `lg`, and Back goes back a pane.** Opening a
   * message pushes a history entry, so a phone's back gesture returns to the
   * list rather than leaving the page and losing every open mailbox.
   */
  function setView(view) {
    if (view === "message" && !wide.matches && history.state?.mbView !== "message") {
      history.pushState({ mbView: "message" }, "");
    }
    app.dataset.view = view;
  }

  window.addEventListener("popstate", () => {
    if (app.dataset.view === "message") app.dataset.view = "list";
  });

  $("to-mailboxes").addEventListener("click", () => setView("mailboxes"));

  // ------------------------------------------------------------ the list

  $("list").addEventListener("click", (event) => {
    const row = event.target.closest("[data-id]");
    if (!row || !current) return;
    const id = Number(row.dataset.id);

    if (event.target.matches('[data-mb="row-check"]')) {
      current.toggle(id);
      return;
    }
    open(id);
  });

  $("list").addEventListener("keydown", (event) => {
    if (!current) return;
    const ids = current.listed();
    // In conversation mode the open message may be any member of a row's conversation.
    const index = ids.findIndex((id) => current.membersOf(id).includes(current.current));
    const move = (to) => {
      if (!ids.length) return;
      const target = Math.max(0, Math.min(ids.length - 1, to));
      list.reveal(target);
      open(ids[target]);
    };

    switch (event.key) {
      case "ArrowDown": case "j": move(index + 1); break;
      case "ArrowUp": case "k": move(index < 0 ? 0 : index - 1); break;
      case "PageDown": move(index + list.pageSize); break;
      case "PageUp": move(index - list.pageSize); break;
      case "Home": move(0); break;
      case "End": move(ids.length - 1); break;
      case "x": case " ":
        if (current.current !== null) current.toggle(current.current);
        break;
      case "Enter":
        if (!wide.matches && current.current !== null) setView("message");
        break;
      default: return;
    }
    event.preventDefault();
  });

  $("select-all").addEventListener("change", (event) => current?.setAllListed(event.target.checked));
  $("select-all-2").addEventListener("change", (event) => current?.setAllListed(event.target.checked));
  $("clear-selection").addEventListener("click", () => current?.clearChecked());
  $("export").addEventListener("click", () => current && exportPdf(current, current.checkedInOrder()));
  $("sort").addEventListener("click", () => current?.setNewestFirst(!current.newestFirst));
  const showAs = (conversations) => {
    if (!current || current.conversations === conversations) return;
    current.setConversations(conversations);
    showMessage(current.current); // the open message, now with or without its company
  };
  $("view-messages").addEventListener("click", () => showAs(false));
  $("view-conversations").addEventListener("click", () => showAs(true));

  // ------------------------------------------------------------ search

  $("query").addEventListener("input", () => {
    clearTimeout(typing);
    // Short enough to feel immediate, long enough not to start a text search per keystroke.
    typing = setTimeout(() => {
      current?.setQuery($("query").value);
      remarkReader();
    }, 150);
  });

  $("query").addEventListener("keydown", (event) => {
    if (event.key === "Escape" && $("query").value) {
      $("query").value = "";
      current?.setQuery("");
      remarkReader();
      event.stopPropagation();
    } else if (event.key === "ArrowDown" || event.key === "Enter") {
      event.preventDefault();
      $("list").focus();
      if (current && current.current === null && current.listed().length) open(current.listed()[0]);
    }
  });

  $("help-toggle").addEventListener("click", () => {
    const open = $("help").classList.toggle("hidden") === false;
    $("help-toggle").setAttribute("aria-expanded", String(open));
  });

  document.addEventListener("keydown", (event) => {
    if (body.dataset.mbMode !== "app") return;
    const typingInField = event.target.closest?.("input, textarea, select, [contenteditable]");
    if (event.key === "/" && !typingInField) {
      event.preventDefault();
      $("query").focus();
      $("query").select();
    }
  });

  // ------------------------------------------------------------ opening files

  const picker = $("file");
  const folderPicker = $("folder");

  for (const button of [$("open"), $("add")]) button.addEventListener("click", () => picker.click());
  $("open-folder").addEventListener("click", () => folderPicker.click());

  picker.addEventListener("change", () => {
    openAll(fromFilePicker(picker.files));
    picker.value = ""; // so choosing the same file again fires `change`
  });

  folderPicker.addEventListener("change", () => {
    openAll(fromFolderPicker(folderPicker.files));
    folderPicker.value = "";
  });

  // The whole window is a drop target, in both states.
  let dragDepth = 0;
  const hasFiles = (event) => [...(event.dataTransfer?.types || [])].includes("Files");

  document.addEventListener("dragenter", (event) => {
    if (!hasFiles(event)) return;
    dragDepth++;
    body.dataset.mbDragging = "";
  });
  document.addEventListener("dragleave", () => {
    if (--dragDepth <= 0) {
      dragDepth = 0;
      delete body.dataset.mbDragging;
    }
  });
  document.addEventListener("dragover", (event) => {
    if (hasFiles(event)) event.preventDefault();
  });
  document.addEventListener("drop", async (event) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragDepth = 0;
    delete body.dataset.mbDragging;
    openAll(await fromDrop(event.dataTransfer));
  });

  wide.addEventListener("change", () => {
    if (wide.matches) app.dataset.view = "list";
    list.draw();
  });

  return { get current() { return current; }, mailboxes };
}
